
import { useState, FormEvent } from 'react';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleNewsletterSubmit = async (e: FormEvent) => {
    e.preventDefault();

    // Basic email validation before sending
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setNewsletterStatus('error');
      return;
    }

    try {
      const formBody = new URLSearchParams();
      formBody.append('email', email);

      const response = await fetch('https://readdy.ai/api/form/d6dl4bqtehdqnvnpj0ag', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setNewsletterStatus('success');
        setEmail('');
        // Reset status after a short delay
        setTimeout(() => setNewsletterStatus('idle'), 4000);
      } else {
        setNewsletterStatus('error');
      }
    } catch (err) {
      console.error('Newsletter submission error:', err);
      setNewsletterStatus('error');
    }
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-slate-900 text-white rounded-t-[3rem] mt-24">
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12">
          {/* Newsletter */}
          <div className="md:col-span-1">
            <h3 className="text-3xl font-light mb-4">Zostań w Kontakcie</h3>
            <p className="text-gray-400 mb-6 leading-relaxed">
              Zapisz się do newslettera i otrzymuj porady oraz informacje o promocjach
            </p>
            <form
              id="newsletter-form"
              data-readdy-form
              onSubmit={handleNewsletterSubmit}
              className="flex flex-col gap-3"
            >
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Twój adres email"
                className="bg-transparent border-b border-gray-600 py-3 px-0 focus:border-white outline-none transition-colors text-white placeholder-gray-500"
                required
              />
              <button
                type="submit"
                className="self-start px-8 py-3 bg-white text-slate-900 rounded-full font-semibold hover:bg-gray-100 transition-all inline-flex items-center gap-2 whitespace-nowrap cursor-pointer"
              >
                Zapisz się
                <i className="ri-arrow-right-line"></i>
              </button>
              {newsletterStatus === 'success' && (
                <p className="text-green-400 text-sm mt-2">Dziękujemy za zapisanie się!</p>
              )}
              {newsletterStatus === 'error' && (
                <p className="text-red-400 text-sm mt-2">Wystąpił błąd. Spróbuj ponownie.</p>
              )}
            </form>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-xl font-bold mb-6">Szybkie Linki</h3>
            <ul className="space-y-3">
              <li>
                <button
                  onClick={() => scrollToSection('services')}
                  className="text-gray-400 hover:text-white transition-colors hover:underline cursor-pointer"
                >
                  Usługi
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('about')}
                  className="text-gray-400 hover:text-white transition-colors hover:underline cursor-pointer"
                >
                  O nas
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('portfolio')}
                  className="text-gray-400 hover:text-white transition-colors hover:underline cursor-pointer"
                >
                  Realizacje
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('contact')}
                  className="text-gray-400 hover:text-white transition-colors hover:underline cursor-pointer"
                >
                  Kontakt
                </button>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-xl font-bold mb-6">Kontakt</h3>
            <div className="space-y-4 mb-6">
              <a
                href="tel:+48691284309"
                className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors cursor-pointer"
              >
                <i className="ri-phone-line"></i>
                +48 691 284 309
              </a>
              <a
                href="mailto:biuro@tmgarant.pl"
                className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors cursor-pointer"
              >
                <i className="ri-mail-line"></i>
                biuro@tmgarant.pl
              </a>
            </div>

            <h3 className="text-xl font-bold mb-4 mt-8">Adres</h3>
            <p className="text-gray-400 leading-relaxed">
              ul. Anny Jagiellonki 6A/4<br />
              65-115 Zielona Góra
            </p>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex gap-6">
              <a
                href="https://facebook.com"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="text-gray-400 hover:text-white transition-colors hover:underline cursor-pointer"
              >
                Facebook
              </a>
              <a
                href="https://instagram.com"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="text-gray-400 hover:text-white transition-colors hover:underline cursor-pointer"
              >
                Instagram
              </a>
            </div>

            <div className="text-center text-gray-400 text-sm">
              © 2024 TM GARANT. Wszelkie prawa zastrzeżone.
            </div>

            <div className="flex gap-6 text-sm">
              <a
                href="#"
                className="text-gray-400 hover:text-white transition-colors hover:underline cursor-pointer"
              >
                Polityka Prywatności
              </a>
              <a
                href="https://readdy.ai/?ref=logo"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-white transition-colors hover:underline cursor-pointer"
              >
                Website Builder
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
